/**
 * Reads and validates the Salesforce customer context attached to a project.
 *
 * The IDs are real, structured data: the agent's `associate_entities` tool
 * writes confirmed account/opportunity IDs onto `Project.associatedEntities`,
 * which the API already returns. But that field round-trips through DynamoDB as
 * free-form JSON, so it arrives loosely typed and has to be narrowed here.
 *
 * Format validation delegates to `validateSfdcId` in `@amzn/afxtaco-utilities`, so
 * the client shows the user exactly the rule the API enforces. Checking here means
 * an obvious typo is caught before it costs the user a turn.
 */

import { SFDC_ENTITIES, Project } from "@amzn/afxtaco-types";
import { validateSfdcId } from "@amzn/afxtaco-utilities";

export interface CustomerContext {
  accountIds: string[];
  opportunityIds: string[];
}

/** Narrows one loosely-typed key into a string array, dropping anything else. */
const readIdList = (
  entities: Record<string, unknown> | undefined,
  key: string,
): string[] => {
  const value = entities?.[key];
  if (!Array.isArray(value)) return [];
  return value.filter(
    (id): id is string => typeof id === "string" && id !== "",
  );
};

/**
 * Extracts the account and opportunity IDs recorded on a project.
 *
 * Returns empty arrays rather than undefined so callers don't each re-handle the
 * absent case; use {@link hasCustomerContext} to branch.
 */
export function readCustomerContext(
  project: Project | undefined,
): CustomerContext {
  const entities = project?.associatedEntities;
  return {
    accountIds: readIdList(entities, SFDC_ENTITIES.ACCOUNT.key),
    opportunityIds: readIdList(entities, SFDC_ENTITIES.OPPORTUNITY.key),
  };
}

/** Whether the project has any Salesforce context to show. */
export const hasCustomerContext = (context: CustomerContext): boolean =>
  context.accountIds.length > 0 || context.opportunityIds.length > 0;

export interface CustomerContextInput {
  accountId: string;
  opportunityId: string;
}

/**
 * Per-field validation errors for the link-account form, keyed by field.
 * A field that is empty is not an error — both are optional, though at least one
 * is required to submit.
 */
export function validateCustomerContext(
  input: CustomerContextInput,
): Partial<Record<keyof CustomerContextInput, string>> {
  const errors: Partial<Record<keyof CustomerContextInput, string>> = {};

  const accountError = validateSfdcId(input.accountId, "ACCOUNT");
  if (accountError) errors.accountId = accountError;

  const opportunityError = validateSfdcId(input.opportunityId, "OPPORTUNITY");
  if (opportunityError) errors.opportunityId = opportunityError;

  return errors;
}

/**
 * Shapes a project's recorded IDs for the agent payload, or undefined when there
 * is nothing to send.
 *
 * Sent on every turn so the agent knows which customer to work with without the
 * user restating it. Undefined rather than empty arrays so the agent can treat
 * presence of the field as meaningful.
 */
export function pingCustomerContext(
  project: Project | undefined,
): { accountIds?: string[]; opportunityIds?: string[] } | undefined {
  const context = readCustomerContext(project);
  if (!hasCustomerContext(context)) return undefined;

  return {
    ...(context.accountIds.length > 0
      ? { accountIds: context.accountIds }
      : {}),
    ...(context.opportunityIds.length > 0
      ? { opportunityIds: context.opportunityIds }
      : {}),
  };
}
